import './SignupView.scss';

import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { createAsyncSelectors } from '@lib/selectors';
import { DeconstructedURL } from '@sonar-software/sonar-ts-lib';
import { ErrorOrFailStatusCode } from '@sonar-software/sonar-http';
import { GatewayCard } from '../general';
import { LocalStorageKey } from '@sonar-software/sonar-ts-types';
import { securityActions } from '@sonar-software/sonar-security';

import { AsyncStateAlert, SanitizedHtml, StatusCodeAlertProps } from '@ui/general';
import {
	AUTOCOMPLETE,
	BASE_ROUTES,
	COPY_GENERIC,
	FULL_ROUTES,
	NOTIFICATIONS_COPY,
	QUERY,
	SIGN_UP_COPY
} from '@sonar-software/sonar-ts-constants';
import { Button, Checkbox, Form, Row } from 'antd';
import { CompanyName, ConfirmPassword, Email, FirstName, LastName } from '@ui/form-items';
import { PrivacyPolicyLink, TermsOfServiceLink } from '@ui/links';
import React, { useCallback, useEffect, useState } from 'react';
import { useAction, useAsyncState } from '@hooks';

interface SignupFormValues {
	companyName: string;
	email: string;
	firstName: string;
	lastName: string;
	password: string;
}

const conflictMessage = NOTIFICATIONS_COPY.ERROR_EMAIL_ALREADY_TAKEN(window.origin.concat('/', BASE_ROUTES.LOGIN));

const alertStatusCodeOptions: StatusCodeAlertProps[] = [
	{
		options: {
			message: <SanitizedHtml
				as='span'
				html={ conflictMessage } />,
			type: 'error'
		},
		statusCode: ErrorOrFailStatusCode.Conflict
	}
];

const enrollSelectors = createAsyncSelectors((state) => state.security.authentication.enroll.post);

export const SignupView: React.FC = () => {
	const [ accepted, setAccepted ] = useState(false);

	const { isErrored, isFailed, isReceived, isRequested } = useAsyncState(enrollSelectors);

	const signup = useAction(securityActions.requestPostSecurityAuthenticationEnroll);
	const resetSignup = useAction(securityActions.resetPostSecurityAuthenticationEnroll);

	useEffect(() => {
		if (isReceived) {
			localStorage.removeItem(LocalStorageKey.LastUsedOrg);
			window.location.replace(FULL_ROUTES.ONBOARDING_CONNECT);
		}
	}, [ isReceived ]);

	const handleValuesChange = useCallback(() => {
		if (isErrored || isFailed) {
			resetSignup();
		}
	}, [ isErrored, isFailed, resetSignup ]);

	const handleAcceptChange = useCallback((e: CheckboxChangeEvent) => {
		setAccepted(e.target.checked);
	}, []);

	const handleFinish = useCallback(
		(values: SignupFormValues) => {
			const { companyName, email, firstName, lastName, password } = values;

			const currentUrl = new DeconstructedURL(window.location.href);
			const region = currentUrl.searchParams.get(QUERY.REGION) ?? '';

			signup({
				customerName: companyName,
				email,
				firstName,
				lastName,
				password,
				shardCategory: region
			});
		},
		[ signup ]
	);

	return (
		<GatewayCard
			id='sign-up'
			title={ SIGN_UP_COPY.SIGN_UP_FOR_AROVY }>
			<Form
				onFinish={ handleFinish }
				onValuesChange={ handleValuesChange }>
				<AsyncStateAlert
					byStatusCode={ alertStatusCodeOptions }
					errored={ NOTIFICATIONS_COPY.ERROR_LOGIN }
					failed={ NOTIFICATIONS_COPY.FAILED_LOGIN }
					stateSelector={ enrollSelectors.state } />
				<CompanyName
					disabled={ isRequested }
					id='sign-up-company-name'
					size='large' />
				<FirstName
					disabled={ isRequested }
					id='sign-up-first-name'
					size='large' />
				<LastName
					disabled={ isRequested }
					id='sign-up-last-name'
					size='large' />
				<Email
					autoComplete={ AUTOCOMPLETE.USERNAME }
					disabled={ isRequested }
					id='sign-up-email'
					size='large' />
				<ConfirmPassword
					disabled={ isRequested }
					id='sign-up-password'
					size='large' />
				<Row className='terms-row'>
					<Checkbox
						checked={ accepted }
						onChange={ handleAcceptChange }>
						{ SIGN_UP_COPY.PARTIAL_AGREEMENT }
						<TermsOfServiceLink />
						{ COPY_GENERIC.AND_SPACED }
						<br />
						<PrivacyPolicyLink />
					</Checkbox>
				</Row>
				<Row>
					<Button
						block
						disabled={ !accepted }
						htmlType='submit'
						id='sign-up-button'
						loading={ isRequested }
						size='large'
						type='primary'>
						{ SIGN_UP_COPY.SIGN_UP }
					</Button>
				</Row>
			</Form>
		</GatewayCard>
	);
};
